import { K8SResourceType, PlanContext } from "yam-types"
import { LOCK_MSG, LOCK_PREFIX } from "../constants"
import KubernetesOperatorClient from "../kube/kube-client"
import { OperatorParam, StateLockType } from "../types.d"
import { createLogger } from "../util"


const LOCK_TIME_KEY = 'lock-time'
const MAX_LOCK_SECONDS = 1800

export default class YamStateLocker {

  private kubeClient: KubernetesOperatorClient
  private lockType: StateLockType
  private locked = false
  private currentPlanContext: PlanContext

  private log = createLogger("state-locker")

  constructor(operatorParam: OperatorParam, kubeClient: KubernetesOperatorClient) {
    this.lockType = operatorParam.engine.clientConf.lockType
    this.kubeClient = kubeClient
    if (!operatorParam.dryRunMode) {
      const releaseLockAndExit = async () => {
        if (!this.locked) {
          this.log.debug(`exit process.`)
          return
        }
        this.log.warn(`SIGTERM/SIGINT received, operation interrupted`)
        if (this.currentPlanContext) {
          await this.unlock(this.currentPlanContext)
          this.log.warn(`SIGTERM/SIGINT handled, lock released.`)
          process.exit(-1)
        }
      }
      process.once('SIGTERM', releaseLockAndExit)
      process.once('SIGINT', releaseLockAndExit)
    }
  }
  async lock(plan: PlanContext): Promise<void> {
    this.currentPlanContext = plan
    this.locked = true
    if (plan.data.templateOutput) {
      return
    }
    const clusterName = this.kubeClient.getCurrentCluster()?.name
    if (this.lockType === "none") {
      this.log.warn('no lock mode may cause inconsistency while operating concurrently.')
      return
    }
    this.log.info(`locking`)
    if (this.lockType === "k8s") {
      const lockObj = {
        name: LOCK_PREFIX + plan.data.workloadName,
        data: {
          [LOCK_TIME_KEY]: new Date().toISOString()
        }
      }
      const lock = await this.kubeClient.getConf(lockObj)
      if (lock && lock.data[LOCK_TIME_KEY]) {
        const lockOffsetSeconds = (Date.now() - new Date(lock.data[LOCK_TIME_KEY]).valueOf()) / 1000
        if (lockOffsetSeconds > MAX_LOCK_SECONDS) {
          this.log.warn(`last lock time expired. the lock is created ${lockOffsetSeconds} before, replacing with new lock.`)
        } else {
          throw new Error(LOCK_MSG + ' lock entry: configMap - ' + lockObj.name)
        }
      }
      // check and create namespace at first
      const ns = plan.data.namespace
      if (!await this.kubeClient.namespaceExists(ns)) {
        await this.kubeClient.apply({
          apiVersion: "v1",
          kind: K8SResourceType.Namespace,
          metadata: {
            name: ns
          }
        })
      }
      await this.kubeClient.saveConf(lockObj)
    } else if (this.lockType === "cloud") {
      // use yam.plus as distributed lock for each application/tenant 
      // (lock level could be set to tenant/team/app level on cloud, or lock when some JIRA issue not approved, to implement operation control)
    }
    this.log.info(`locked for '${plan.data.workloadName}' of cluster: ${clusterName}`)
  }

  async unlock(plan: PlanContext): Promise<void> {
    if (plan.data.templateOutput) {
      return
    }
    const clusterName = this.kubeClient.getCurrentCluster()?.name
    if (this.lockType === "none") {
      return
    }
    try {
      if (this.lockType === "k8s") {
        await this.kubeClient.delete({
          kind: K8SResourceType.ConfigMap,
          name: LOCK_PREFIX + plan.data.workloadName
        })
      } else if (this.lockType === "cloud") {
        // TODO unlock cloud lock entry
      }
      this.locked = false
      this.log.info(`unlocked for workload '${plan.data.workloadName}' in cluster: ${clusterName}`)
    } catch (e) {
      this.log.warn(`unlock failed, ${e.message}`)
    }
  }
}
